import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

export interface UserRole {
  groups: string[];
  primaryRole: string | null;
  displayName: string | null;
}

export const useUserRole = () => {
  const { user: authUser, loading: authLoading } = useAuth();
  const [roleInfo, setRoleInfo] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getRoleDisplayName = (role: string): string => {
    switch (role) {
      case 'SYSTEM_ADMIN':
        return 'システム管理者';
      case 'MANAGER':
        return '管理者';
      case 'USER':
        return 'ユーザー';
      default:
        return role;
    }
  };

  const loadRoleInfo = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1秒

    if (!authUser) {
      setRoleInfo(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      
      // Add small delay to let session stabilize on first attempt
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;

      // Cognito groups from JWT token
      const cognitoGroups = (payload?.['cognito:groups'] as string[]) || [];
      
      // Phase 2: Determine primary role (highest precedence)
      // Must match backend Cognito group names: SYSTEM_ADMIN, MANAGER, USER
      let primaryRole: string | null = null;
      if (cognitoGroups.includes('SYSTEM_ADMIN')) {
        primaryRole = 'SYSTEM_ADMIN';
      } else if (cognitoGroups.includes('MANAGER')) {
        primaryRole = 'MANAGER';
      } else if (cognitoGroups.includes('USER')) {
        primaryRole = 'USER';
      }

      const displayName = primaryRole ? getRoleDisplayName(primaryRole) : null;

      
      setRoleInfo({
        groups: cognitoGroups,
        primaryRole,
        displayName
      });
      setError(null);
    } catch (err: any) {
      logger.error(`❌ [useUserRole] Failed to load role information (attempt ${retryCount + 1}):`, err);

      // Retry logic for session timing issues
      if (retryCount < MAX_RETRIES && 
          (err.message?.includes('No current user') || 
           err.message?.includes('not authenticated'))) {
        setTimeout(() => {
          loadRoleInfo(retryCount + 1);
        }, RETRY_DELAY);
        return;
      }
      
      setRoleInfo(null);
      setError(`ロール情報の取得に失敗しました (${retryCount + 1}/${MAX_RETRIES + 1})`);
    } finally {
      setLoading(false);
    }
  }, [authUser]);

  // 🔄 Phase 1-5: Load role info when auth state changes
  useEffect(() => {
    // Don't load role info while auth is still loading
    if (authLoading) {
      setLoading(true);
      return;
    }
    
    // Load role info when authenticated user is available
    if (authUser) {
      loadRoleInfo();
    } else {
      // Clear role info when user is not authenticated
      setRoleInfo(null);
      setError(null);
      setLoading(false);
    }
  }, [authUser, authLoading, loadRoleInfo]);

  return {
    roleInfo,
    loading: loading || authLoading,
    error,
    reload: () => loadRoleInfo(0) // Reset retry count on manual reload
  };
};